import { ORGANIZATIONAL_STATUSES } from '../constants/organizationalStatuses';
import {
  INDIVIDUAL_OPEN_MODES,
  INDIVIDUAL_PAGE_MODES,
  INDIVIDUAL_OPEN_MODE_BY_ENTRY_POINT,
} from '../constants/individualOpenModes';

/**
 * ADR-109 §1 — the single rule for "open this Individual as Edit or View".
 * Used by the auto route (`/employees/:id`), the Individuals list card and the Project page.
 *
 * Edit only when the caller manages the individual (useEmployeePermissions.canManageEmployee)
 * and the individual is not resigned (the Project page rule, ADR-017). Cancelled individuals
 * resolve to Edit, where EmployeeForm already hides "Update Individual".
 *
 * @param {Object} args
 * @param {Object|null} args.employee - needs `cluster`, `directManager`, `organizationalStatus`
 * @param {Function} args.canManageEmployee
 * @returns {'edit'|'view'}
 */
export const resolveIndividualOpenMode = ({ employee, canManageEmployee }) => {
  if (!employee || typeof canManageEmployee !== 'function' || !canManageEmployee(employee)) {
    return INDIVIDUAL_PAGE_MODES.VIEW;
  }
  if (employee.organizationalStatus === ORGANIZATIONAL_STATUSES.RESIGNED) {
    return INDIVIDUAL_PAGE_MODES.VIEW;
  }
  return INDIVIDUAL_PAGE_MODES.EDIT;
};

/** Open policy for an entry point; unknown entry points are read-only (least privilege). */
export const getIndividualOpenPolicy = (entryPoint) =>
  INDIVIDUAL_OPEN_MODE_BY_ENTRY_POINT[entryPoint] || INDIVIDUAL_OPEN_MODES.VIEW;

const withTab = (path, tab) => (tab ? `${path}?tab=${encodeURIComponent(tab)}` : path);

/**
 * ADR-109 §2 — navigation target for opening an Individual from a report.
 *
 * @param {Object} args
 * @param {string} args.employeeId
 * @param {string} args.entryPoint - INDIVIDUAL_ENTRY_POINTS value
 * @param {string} [args.tab] - EmployeeForm TABS key
 * @param {string} args.returnTo - where "Back" goes
 * @param {string} args.returnLabel - "Back to <label>"
 * @param {Object} [args.state] - report state to restore on return
 * @returns {{ path: string, options: { state: Object } }}
 */
export const buildOpenIndividualNavigation = ({ employeeId, entryPoint, tab, returnTo, returnLabel, state = {} }) => {
  if (!employeeId) {
    throw new TypeError('buildOpenIndividualNavigation: employeeId is required');
  }
  if (!returnTo || !returnLabel) {
    throw new TypeError('buildOpenIndividualNavigation: returnTo and returnLabel are required');
  }
  const base = `/employees/${employeeId}`;
  const path = getIndividualOpenPolicy(entryPoint) === INDIVIDUAL_OPEN_MODES.AUTO
    ? base
    : `${base}/${INDIVIDUAL_PAGE_MODES.VIEW}`;
  return {
    path: withTab(path, tab),
    options: { state: { ...state, returnTo, returnLabel } },
  };
};

/**
 * ADR-109 §1 — where the auto route redirects once the mode is resolved.
 * Keeps the query string (?tab=) and passes the navigation state through unchanged.
 */
export const buildResolvedIndividualNavigation = ({ employeeId, mode, search = '', state }) => ({
  path: `/employees/${employeeId}/${mode}${search || ''}`,
  options: { replace: true, state },
});

/**
 * ADR-109 §3 — `individualReturn` for the Technology Configuration round-trip: the Individual
 * URL with the Technology Radar tab selected, plus the navigation state the Individual was
 * opened with (report returnTo/returnLabel/report state).
 */
export const buildIndividualReturn = ({ pathname, search = '', tab, state }) => {
  const params = new URLSearchParams(search || '');
  if (tab) {
    params.set('tab', tab);
  }
  const query = params.toString();
  return {
    path: query ? `${pathname}?${query}` : pathname,
    state: state ?? null,
  };
};
